<template>
   <dashboard-layout><v-container fluid class="down-top-padding py-0">
      <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>
      <v-card><v-toolbar dark color="primary"><v-icon left>mdi-account-arrow-up</v-icon><v-toolbar-title>Graduate to Corporate Upgrade</v-toolbar-title></v-toolbar>
         <v-card-text>
            <v-card outlined class="applicant mb-4"><v-card-text><v-row align="center">
               <v-col cols="12" sm="auto">
                  <v-avatar size="76" color="blue lighten-5">
                     <v-img :src="profilePhotoSrc" :alt="memberinfo.fullname"></v-img>
                  </v-avatar>
               </v-col>
               <v-col>
                  <div class="overline">Applicant Details</div>
                  <div class="text-h6 font-weight-bold">{{ memberinfo.fullname }}</div>
                  <div>{{ memberinfo.contactemail }}</div>
                  <div class="mt-1">MIP Member ID: <strong>{{ memberinfo.memberid||'Not assigned' }}</strong></div>
               </v-col>
               <v-col cols="12" md="3">
                  <div class="caption">Application Status</div>
                  <v-chip small dark :color="statusColor">{{ statusLabel }}</v-chip>
               </v-col>
            </v-row></v-card-text></v-card>

            <v-alert v-if="status===5 && !bothEndorsed" type="info" dense text>Your Corporate Upgrade endorsement request has been sent. Waiting for proposer and seconder approval.</v-alert>
            <v-alert v-if="status===5 && bothEndorsed && !hasCorporateUpgradePaid && !hasCorporateUpgradePendingPayment" type="warning" dense text>
               Your proposer and seconder have approved. Please complete the Corporate Upgrade payment before submitting the application.
               <div class="mt-2"><v-btn small color="primary" dark href="/member/payments?purpose=corporate-upgrade">Go to Payment</v-btn></div>
            </v-alert>
            <v-alert v-if="status===5 && bothEndorsed && hasCorporateUpgradePendingPayment" type="info" dense text>
               Your Corporate Upgrade payment receipt is pending review. You may submit the application for Secretariat review.
            </v-alert>
            <v-alert v-if="status===5 && bothEndorsed && hasCorporateUpgradePaid" type="success" dense text>
               Proposer and seconder have approved, and payment has been recorded. You may now submit the application for Secretariat review.
            </v-alert>
            <v-alert v-if="status===1" type="info" dense text>Your Corporate Upgrade application has been submitted for Secretariat review and MIP meeting decision.</v-alert>
            <v-alert v-if="status===4" type="warning" dense text>Your application requires corrections. Review the notes and resubmit.</v-alert>
            <v-alert v-if="status===3" type="error" dense text>
               Your Corporate Upgrade application was rejected.
               <div class="mt-2">Please contact the Secretariat to clarify or request a reopen.</div>
               <div class="mt-1"><v-icon small>mdi-email-outline</v-icon> <a href="mailto:admin@members.mip.org.my">admin@members.mip.org.my</a></div>
            </v-alert>
            <v-alert v-if="status===2" type="success" dense text>
               Your Corporate Upgrade has been approved. Your membership has been upgraded to Corporate.
            </v-alert>
            <v-alert v-if="memberinfo.applcorp_notes" type="info" dense outlined><strong>Secretariat notes:</strong> {{ memberinfo.applcorp_notes }}</v-alert>

            <!-- Endorsement Status (visible when submitted) -->
            <v-row v-if="[1,5].includes(status)" dense class="mb-4">
               <v-col cols="12" md="6">
                  <v-card outlined><v-card-text class="py-3">
                     <div class="caption grey--text">Proposer Approval</div>
                     <v-chip small dark :color="endorsementColor(memberinfo.applcorp_proposer_status)">
                        {{ endorsementLabel(memberinfo.applcorp_proposer_status) }}
                     </v-chip>
                     <div v-if="memberinfo.applcorp_proposer_responded_at" class="caption mt-1">
                        Responded: {{ formatDate(memberinfo.applcorp_proposer_responded_at) }}
                     </div>
                  </v-card-text></v-card>
               </v-col>
               <v-col cols="12" md="6">
                  <v-card outlined><v-card-text class="py-3">
                     <div class="caption grey--text">Seconder Approval</div>
                     <v-chip small dark :color="endorsementColor(memberinfo.applcorp_seconder_status)">
                        {{ endorsementLabel(memberinfo.applcorp_seconder_status) }}
                     </v-chip>
                     <div v-if="memberinfo.applcorp_seconder_responded_at" class="caption mt-1">
                        Responded: {{ formatDate(memberinfo.applcorp_seconder_responded_at) }}
                     </div>
                  </v-card-text></v-card>
               </v-col>
            </v-row>

            <v-row dense class="mb-3"><v-col cols="12" md="4" v-for="item in schedule" :key="item.label"><v-card outlined><v-card-text><div class="caption grey--text">{{ item.label }}</div><strong>{{ formatDate(item.value) }}</strong></v-card-text></v-card></v-col></v-row>
            <v-card v-if="documentUrl" outlined class="mb-4"><v-card-text><div class="caption grey--text">Support Document</div><v-btn text small color="primary" :href="documentUrl" target="_blank"><v-icon left small>mdi-file-document-outline</v-icon>{{ memberinfo.applcorp_document }}</v-btn></v-card-text></v-card>

            <table-corp-exp ref="corpexp" class="mb-4" :readonly="!canEditApplication" @CorpExpStatus="onCorpExpStatus"></table-corp-exp>
            <v-alert v-if="canEditApplication && !corpExperienceReady" type="warning" dense text>
               Please complete at least 3 corporate experience scopes and upload the verified log sheet before submitting.
            </v-alert>

            <v-form v-if="canEditApplication" ref="form">
               <v-textarea v-model="form.notes" label="Notes" outlined rows="3" hint="Add relevant information or explain corrections requested by admin."></v-textarea>
               <v-file-input v-model="form.document" :label="status===4?'Replacement Support Document *':'Support Document'" accept=".jpg,.jpeg,.png,.pdf" outlined show-size :rules="status===4?[v=>!!v||'Replacement document is required.']:[]"></v-file-input>

               <v-row dense class="mt-2">
                  <v-col cols="12" md="6">
                     <div class="section-heading mb-2">
                        <v-avatar color="primary" size="28" class="mr-2"><span class="white--text caption">P</span></v-avatar>
                        <div><strong>Proposer</strong><div class="caption grey--text">Active Corporate or Fellow member</div></div>
                     </div>
                     <v-autocomplete v-model="form.proposer_userid" :items="eligibleMembers" item-value="userid" :item-text="memberLabel" label="Search proposer by name or Member ID *" outlined dense clearable :rules="[v=>!!v||'Proposer is required.']">
                        <template v-slot:item="{ item }"><member-option :member="item"></member-option></template>
                        <template v-slot:selection="{ item }"><member-selection :member="item"></member-selection></template>
                     </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                     <div class="section-heading mb-2">
                        <v-avatar color="primary" size="28" class="mr-2"><span class="white--text caption">S</span></v-avatar>
                        <div><strong>Seconder</strong><div class="caption grey--text">Different active Corporate or Fellow member</div></div>
                     </div>
                     <v-autocomplete v-model="form.seconder_userid" :items="eligibleMembers" item-value="userid" :item-text="memberLabel" label="Search seconder by name or Member ID *" outlined dense clearable :rules="[v=>!!v||'Seconder is required.', differentMemberRule]">
                        <template v-slot:item="{ item }"><member-option :member="item"></member-option></template>
                        <template v-slot:selection="{ item }"><member-selection :member="item"></member-selection></template>
                     </v-autocomplete>
                  </v-col>
               </v-row>
               <v-alert v-if="eligibleMembers.length < 2" type="error" dense text>At least two eligible active Corporate/Fellow members are required.</v-alert>
            </v-form>
         </v-card-text>
         <v-card-actions><v-spacer></v-spacer>
            <v-btn text href="/member/info">Membership Information</v-btn>
            <v-btn v-if="canEditApplication" color="primary" :loading="submitting" :disabled="submitting||eligibleMembers.length<2||sameMember||!corpExperienceReady" @click="submit">{{ status===4?'Submit Resubmission':'Request Endorsement' }}</v-btn>
            <v-btn v-if="canFinalSubmit" color="success" :loading="submitting" :disabled="submitting" @click="finalSubmit">Submit Application</v-btn>
         </v-card-actions>
      </v-card><v-snackbar v-model="snackbar" color="blue darken-3" top>{{ message }}</v-snackbar>
   </v-container></dashboard-layout>
</template>
<script>
const axios = require('axios')
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import TableCorpExp from './Tables/TableCorpExp.vue'

const MemberOption = {
   props: ['member'],
   template: `<v-list-item class="px-0">
      <v-list-item-avatar size="40"><v-img :src="member.photo_url" :alt="member.fullname"></v-img></v-list-item-avatar>
      <v-list-item-content>
         <v-list-item-title class="font-weight-medium">{{ member.fullname }}</v-list-item-title>
         <v-list-item-subtitle>MIP ID: {{ member.memberid || 'Not assigned' }} · {{ member.membertype === 'COR' ? 'Corporate' : 'Fellow' }}</v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action><v-chip x-small :color="member.membertype === 'COR' ? 'blue' : 'purple'" dark>{{ member.membertype }}</v-chip></v-list-item-action>
   </v-list-item>`,
}

const MemberSelection = {
   props: ['member'],
   template: `<div class="d-flex align-center">
      <v-avatar size="24" class="mr-2"><v-img :src="member.photo_url" :alt="member.fullname"></v-img></v-avatar>
      <span class="text-truncate"><strong>{{ member.fullname }}</strong> – {{ member.memberid || 'No ID' }} ({{ member.membertype }})</span>
   </div>`,
}

export default {
   name: 'MOMCorporateUpgrade',
   components: { DashboardLayout, BaseBreadcrumb, MemberOption, MemberSelection, TableCorpExp },
   props: {
      memberinfo: { type: Object, required: true },
      eligibleMembers: { type: Array, default: () => [] },
      corporateUpgradePayment: { type: Object, default: null },
   },
   data() {
      return {
         page: { title: 'Corporate Upgrade' },
         breadcrumbs: [{ text: 'Dashboard', disabled: false, to: '/dashboard' }, { text: 'Corporate Upgrade', disabled: true }],
         submitting: false, snackbar: false, message: '',
         corpExpStatus: {
            hasSummary: false,
            hasLogSheet: false,
         },
         form: {
            notes: this.memberinfo.applcorp_notes || '',
            document: null,
            proposer_userid: this.memberinfo.applcorp_proposer_userid || null,
            seconder_userid: this.memberinfo.applcorp_seconder_userid || null,
         },
      }
   },
   computed: {
      currentUser() { return (this.$page && (this.$page.props || this.$page).user) || {} },
      profilePhotoSrc() { const u = this.currentUser; return u.id ? `/member/photo/${u.id}?v=${Date.now()}` : (u.profile_photo_url || '') },
      status() { return parseInt(this.memberinfo.applcorpstatus || 0) },
      canEditApplication() { return this.status === 0 || this.status === 4 },
      canFinalSubmit() { return this.status === 5 && this.bothEndorsed && (this.hasCorporateUpgradePaid || this.hasCorporateUpgradePendingPayment) },
      sameMember() { return !!this.form.proposer_userid && String(this.form.proposer_userid) === String(this.form.seconder_userid) },
      differentMemberRule() { return () => !this.sameMember || 'Proposer and seconder must be different members.' },
      statusLabel() { return ({ 0: 'Not Submitted', 1: 'Pending Secretariat Review', 2: 'Approved', 3: 'Rejected', 4: 'Need Resubmission', 5: 'Pending Endorsement / Payment' })[this.status] || 'Unknown' },
      statusColor() { return ({ 0: 'grey', 1: 'orange darken-2', 2: 'green', 3: 'red', 4: 'deep-purple', 5: 'blue darken-2' })[this.status] || 'grey' },
      bothEndorsed() { return parseInt(this.memberinfo.applcorp_proposer_status || 0) === 1 && parseInt(this.memberinfo.applcorp_seconder_status || 0) === 1},
      corpExperienceReady() { return this.corpExpStatus.hasSummary && this.corpExpStatus.hasLogSheet },
      hasCorporateUpgradePaid() { return this.corporateUpgradePayment && String(this.corporateUpgradePayment.trxnstatus || '') === '01' },
      hasCorporateUpgradePendingPayment() { return this.corporateUpgradePayment && ['00', '02'].includes(String(this.corporateUpgradePayment.trxnstatus || '')) },
      schedule() { return [{ label: 'Course Date', value: this.memberinfo.applcorp_course_date }, { label: 'Interview Date', value: this.memberinfo.applcorp_interview_date }, { label: 'Exam Date', value: this.memberinfo.applcorp_exam_date }] },
      documentUrl() { return this.memberinfo.applcorp_document && this.memberinfo.applcorp_documentpath ? `/${this.memberinfo.applcorp_documentpath}/${this.memberinfo.applcorp_document}` : null },
   },
   methods: {
      memberLabel(item) { return `${item.fullname} – ${item.memberid || 'No ID'} (${item.membertype})` },
      endorsementLabel(value) { return ({ 0: 'Pending', 1: 'Approved', 2: 'Rejected' })[parseInt(value || 0)] || 'Pending' },
      endorsementColor(value) { return ({ 0: 'orange darken-2', 1: 'green', 2: 'red' })[parseInt(value || 0)] || 'orange darken-2' },
      formatDate(value) { if (!value) return '-'; return new Date(value).toLocaleDateString('en-MY', { day: '2-digit', month: '2-digit', year: 'numeric' }) },
      onCorpExpStatus(status) { this.corpExpStatus = status || { hasSummary: false, hasLogSheet: false } },
      submit() {
         if (!this.corpExperienceReady) {
            this.message = 'Please complete the corporate experience summary and upload the log sheet before submitting.'
            this.snackbar = true
            return
         }
         if (!this.$refs.form.validate() || this.sameMember) return
         const d = new FormData()
         d.append('userid', this.memberinfo.userid)
         d.append('notes', this.form.notes || '')
         d.append('proposer_userid', this.form.proposer_userid)
         d.append('seconder_userid', this.form.seconder_userid)
         if (this.form.document) d.append('document', this.form.document)
         this.submitting = true
         axios.post('/member/apply/upgrade-corporate', d, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(r => {
               if (r.data && r.data.status === 'OK') { Object.assign(this.memberinfo, r.data.data); this.message = 'Corporate Upgrade endorsement request submitted.' }
               else { this.message = (r.data && r.data.error) || 'Unable to submit application.' }
               this.snackbar = true
            })
            .catch(e => { this.message = (e.response && e.response.data && e.response.data.error) || 'Unable to submit application.'; this.snackbar = true })
            .finally(() => { this.submitting = false })
      },
      finalSubmit() {
         if (!this.canFinalSubmit) {
            this.message = 'Please complete proposer and seconder approval, then Corporate Upgrade payment before submitting.'
            this.snackbar = true
            return
         }

         this.submitting = true
         axios.post('/member/apply/upgrade-corporate/submit', { userid: this.memberinfo.userid })
            .then(r => {
               if (r.data && r.data.status === 'OK') {
                  Object.assign(this.memberinfo, r.data.data)
                  this.message = 'Corporate Upgrade application submitted for Secretariat review.'
               } else {
                  this.message = (r.data && r.data.error) || 'Unable to submit application.'
               }
               this.snackbar = true
            })
            .catch(e => {
               this.message = (e.response && e.response.data && e.response.data.error) || 'Unable to submit application.'
               this.snackbar = true
            })
            .finally(() => { this.submitting = false })
      },
   },
   mounted() {
      if (this.$refs.corpexp) {
         this.$refs.corpexp.setuserid(this.memberinfo.userid)
      }
   },
}
</script>
<style scoped>
.applicant { border-left: 4px solid #1976d2 !important; }
.section-heading { display: flex; align-items: center; }
</style>
